import { useCallback, useMemo, useRef, useState, type FC } from 'react';
import classNames from 'classnames';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { DualSidebarLayout } from 'components/layout/dual-sidebar-layout';
import type {
  ColorByRule,
  ColorByOption,
} from 'components/color-by-attribute-modal';
import type { JSONValue } from 'lib/types';
import { ExportImageDrawer } from 'components/chart-export';
import type {
  PreDrillWorkspace,
  PreDrillVerificationItems,
  WellborePoint,
} from 'types/api-responses';
import { PreDrillWorkspaceHeader } from './pre-drill-workspace-header';
import { GeometrySheetHeader } from './geometry-sheet-header';
import { SettingsPanel } from './settings-panel';
import { WellboreDetailsCard } from './wellbore-details-card';
import { WorkspaceMap, type WorkspaceMapHandle } from './workspace-map';
import { WellborePointsPanel } from './wellbore-points-panel';
import { EditWellborePointPanel } from './edit-wellbore-point-panel';
import { PropertyDetailsDrawer } from './property-details-drawer';
import { useGeometryWrites } from './use-geometry-writes';
import { useGeometryDepthControls } from './use-geometry-depth-controls';
import { useGeometrySheetReads } from './use-geometry-sheet-reads';
import { useGeometryDeriveWait } from './use-geometry-derive-wait';
import { useTargetZoneActions } from './use-target-zone-actions';
import { TargetZoneIdentification } from './target-zone-identification';
import {
  SubsurfaceExplorer,
  type SubsurfaceExplorerHandle,
} from './subsurface-explorer';
import { AgentOutputPanel } from './agent-output-panel';
import { AgentRunsPanel } from './agent-runs-panel';
import { ScoringGlossary } from './scoring-glossary';
import { SgcPickerDrawer } from './sgc-picker-drawer';
import { ChangeMatchDrawer } from './change-match/change-match-drawer';
import { VerificationTasksDrawer } from './verification-tasks-drawer';
import { PromoteModal } from './promote-modal';
import { ContentBanners } from './content-banners';
import { GeometryViewsRow } from './geometry-views-row';
import { LandingInterpretationSection } from './landing-interpretation/landing-interpretation-section';
import { SelectGeologicIntervalDrawer } from './landing-interpretation/select-geologic-interval-drawer';
import type { LandingIntervalPickerRequest } from './landing-interpretation/types';
import { RelatedItemsSection } from 'components/related-items';
import {
  updatePreDrillWorkspaceAtom,
  promotePreDrillWorkspaceAtom,
  preDrillColorFacetsAtom,
  preDrillColorByUwiMapAtom,
  preDrillMapRefAtom,
  useTargetZoneSuggestionsAtom,
  useVerificationItemsAtom,
} from './atoms';
import { usePreDrillNotifications } from './use-pre-drill-notifications';
import {
  resolveSectionVisibility,
  readColorRules,
  toColorRuleState,
  taskVerificationItems,
  getPreDrillColorOptions,
  boundingBoxFromMapRef,
  COLOR_ATTRIBUTE_FACET_PARAM,
  COLOR_ATTRIBUTE_FACET_FIELDS,
  COLOR_BY_RADIUS,
  WELL_QUALIFIER_ATTRIBUTE,
  resolveColorByUwiMembership,
  targetZoneHrefsFor,
} from './utils';
import type {
  WellsFacetsResponse,
  FilteredUwi,
} from 'types/pre-drill-workspace';
import request from 'lib/request';
import type { PreDrillRightPanel } from './types';
import type { PreDrillWorkspaceSettingsEdit } from 'types/api-payloads';
import styles from './pre-drill-workspace-content.module.scss';

interface PreDrillWorkspaceContentProps {
  workspace: PreDrillWorkspace;
  refetchShow: () => void;
  /** Leaves the workspace, returning to the host page (clears the active atom). */
  onLeave: () => void;
  /** Hosted inside a tearsheet rather than laid out on a page of its own. */
  embedded?: boolean;
  /** The well the sheet was opened for, shown in its header beside the UWI. */
  wellName?: string | null;
  /**
   * The Well Workspace hosting the sheet. Every geometry address is built from this
   * and the bore's UWI14, so the geometry surfaces stay out without it.
   */
  wellWorkspaceId?: number | null;
}

/** Right panels that are not the shell default (420px). Undefined keeps the default. */
const PANEL_WIDTHS: Record<string, number | undefined> = {
  changeMatch: 640,
  landingInterval: 640,
  editPoint: 440,
  propertyDetails: 440,
};

// Orchestrator for the thin show: header + DualSidebarLayout. Sections read
// their own sub-resources; shared ones are fetched here once.
export const PreDrillWorkspaceContent: FC<PreDrillWorkspaceContentProps> = ({
  workspace,
  refetchShow,
  onLeave,
  embedded = false,
  wellName,
  wellWorkspaceId,
}) => {
  const [settingsOpen, setSettingsOpen] = useState(true);
  const [rightPanel, setRightPanel] = useState<PreDrillRightPanel>(null);
  const [visibility, setVisibility] = useState(() =>
    resolveSectionVisibility()
  );
  const [colorRules, setColorRules] = useState<ColorByRule[]>(() =>
    readColorRules(workspace)
  );
  const [promoteModalOpen, setPromoteModalOpen] = useState(false);
  const [mapExpanded, setMapExpanded] = useState(false);
  // The sheet's Save is held while the map is in edit mode, until Done Editing.
  const [geometryEditing, setGeometryEditing] = useState(false);
  const [explorerExpanded, setExplorerExpanded] = useState(false);
  // What the landing interpretation opened the Select Geologic Interval drawer with.
  const [landingPicker, setLandingPicker] =
    useState<LandingIntervalPickerRequest | null>(null);
  const updateWorkspace = useSetAtom(updatePreDrillWorkspaceAtom);
  const [savingSgc, setSavingSgc] = useState(false);
  // The reason a target-zone generation (re-run) failed, from the Pusher event.
  const [generationError, setGenerationError] = useState<{
    code?: string;
    message?: string;
  } | null>(null);
  // Reason a geometry_construction (Recalculate) failed — surfaced as a banner
  // in the Subsurface Explorer; the prior geometry stays on the charts.
  const [geometryError, setGeometryError] = useState<{
    code?: string;
    message?: string;
  } | null>(null);
  // Pusher events awaited after enqueuing a Recalculate / Re-run — set on enqueue
  // so the hook subscribes immediately (status flips async), cleared on push.
  const [pendingPushEvents, setPendingPushEvents] = useState<string[]>([]);
  const addPendingPushEvent = useCallback((event: string) => {
    setPendingPushEvents((prev) => {
      return prev.includes(event) ? prev : [...prev, event];
    });
  }, []);
  const releasePendingPushEvent = useCallback((event: string) => {
    setPendingPushEvents((prev) => prev.filter((pending) => pending !== event));
  }, []);
  // Color modal value lists from a dedicated bbox-scoped /queries/wells facets
  // call, fired on modal open (fetchColorFacets). Null until it lands.
  const colorFacets = useAtomValue(preDrillColorFacetsAtom);
  const setColorFacets = useSetAtom(preDrillColorFacetsAtom);
  const colorOptionsLoading = colorFacets === null;
  const mapRef = useAtomValue(preDrillMapRefAtom);
  const setColorByUwiMap = useSetAtom(preDrillColorByUwiMapAtom);
  // Imperative handle to the Subsurface Explorer — refreshCharts() re-pulls the
  // charts after a geometry event (not covered by the show refetch).
  const subsurfaceRef = useRef<SubsurfaceExplorerHandle>(null);
  const [promoteState, promoteWorkspace] = useAtom(
    promotePreDrillWorkspaceAtom
  );

  const targetZoneHrefs = targetZoneHrefsFor(workspace, wellWorkspaceId);
  // The Change Match catalog's key inside the sheet, memoised because the drawer
  // re-queries whenever its key changes.
  const catalogBore = useMemo(() => {
    if (!wellWorkspaceId) return undefined;
    return { wellWorkspaceId, uwi14: workspace.uwi14 };
  }, [wellWorkspaceId, workspace.uwi14]);
  const suggestionsAtom = useTargetZoneSuggestionsAtom(
    targetZoneHrefs.suggestions
  );
  const [suggestionsResource, refetchSuggestions] = useAtom(suggestionsAtom);
  const suggestions =
    suggestionsResource.data && !('error' in suggestionsResource.data)
      ? suggestionsResource.data
      : null;

  const itemsAtom = useVerificationItemsAtom(
    workspace.resources.verificationItems?.href
  );
  const [itemsResource, refetchItems] = useAtom(itemsAtom);
  const allItems: PreDrillVerificationItems =
    itemsResource.data && !('error' in itemsResource.data)
      ? itemsResource.data
      : [];
  const {
    pointsHref,
    pointsLoading,
    pointsData,
    mapPointsState,
    refetchPoints,
    setAnsweredPoints,
    gridsHref,
    intersectedGrids,
    refetchGrids,
    detailsAtom,
    refetchDetails,
    geometryEditable,
    canEditGeometry,
    canRefreshGeometry,
    geometryFailedOnRead,
    noDirectionalSurvey,
    surveyUntouched,
    pointsHaveDepths,
    surveyed,
  } = useGeometrySheetReads({ workspace, wellWorkspaceId });
  // UAT 25: an untouched survey is kept by default; while Keep Survey Depths is on the
  // depth controls are held and a confirmed zone records the zone alone.
  const [keepSurveyDepths, setKeepSurveyDepths] = useState(true);
  // What a write says about the survey: nothing where there is none to keep.
  const rebuildSurvey = surveyUntouched ? !keepSurveyDepths : null;
  // The point being edited, held by position: the points' ids change on every
  // rebuild, so an id captured before a refetch can address another vertex.
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [canRevert, setCanRevert] = useState(false);
  // The sheet's Save and Cancel act on the map's uncommitted edit.
  const geometryMapRef = useRef<WorkspaceMapHandle>(null);
  // Set while the sheet's own Save is what committed, so the success handler knows to
  // leave. The map's Done Editing commits the same way but stays where it is.
  const closeAfterCommit = useRef(false);
  // Pusher: a lifecycle event re-pulls the thin show + only the sub-resources it
  // changes (suggestions refetch gated to target_zone_generation to avoid flash).
  const handleServerChange = useCallback(
    (eventName: string) => {
      setGenerationError(null);
      setGeometryError(null);
      releasePendingPushEvent(eventName);
      refetchShow();
      void refetchItems();
      if (eventName === 'target_zone_generation') refetchSuggestions();
      // Charts only change when the geometry is (re)built — refresh them on the
      // geometry event alone (target_zone_generation doesn't touch the geometry).
      if (eventName === 'geometry_construction') {
        subsurfaceRef.current?.refreshCharts();
        // The points are derived from the rebuilt geometry — re-read them too.
        if (pointsHref) refetchPoints();
        // The details card's derived fields (lateral length, elevations) are
        // recomputed from the rebuilt geometry — re-read them alongside the points.
        if (pointsHref) refetchDetails();
      }
      // The grid ranges follow the datum and the collection — both can change here.
      if (gridsHref) refetchGrids();
    },
    [
      refetchShow,
      refetchSuggestions,
      refetchItems,
      refetchPoints,
      refetchDetails,
      pointsHref,
      refetchGrids,
      gridsHref,
      releasePendingPushEvent,
    ]
  );
  // Failed completion: capture the reason to surface it, and re-fetch the show
  // so the status leaves "processing" and the Pusher unsubscribes.
  const handleServerFailure = useCallback(
    (error: { code?: string; message?: string } | null, eventName: string) => {
      // Geometry failures surface in the Subsurface Explorer; target-zone
      // failures surface near the Target Zone section.
      if (eventName === 'geometry_construction') {
        setGeometryError(error);
      } else {
        setGenerationError(error);
      }
      releasePendingPushEvent(eventName);
      refetchShow();
    },
    [refetchShow, releasePendingPushEvent]
  );
  // Recalculate enqueued: clear prior geometry failure, mark the geometry push
  // pending (subscribes immediately), + re-fetch the show.
  const handleGeometryRefreshEnqueued = useCallback(() => {
    setGeometryError(null);
    addPendingPushEvent('geometry_construction');
    refetchShow();
  }, [refetchShow, addPendingPushEvent]);
  usePreDrillNotifications(
    workspace,
    { onShowChanged: handleServerChange, onFailure: handleServerFailure },
    pendingPushEvents
  );

  // Permit vertical depth — pre-fills the Target TVD field when the geometry is
  // generalized (no grid) and no manual depth is set.
  const permittedDepthTvdFt =
    suggestions?.permitOnFile?.permittedDepthTvdFt ?? null;
  // Story 7 hosts the depth controls in the Wellbore Points panel; the Subsurface
  // Explorer then follows this one staged state instead of owning its own.
  const depthControls = useGeometryDepthControls({
    workspace,
    refreshHref: pointsData?.resources.geometryRefreshes.href ?? null,
    permittedDepthTvdFt,
    onRefreshEnqueued: handleGeometryRefreshEnqueued,
  });
  const geometryPending = pendingPushEvents.includes('geometry_construction');

  const releaseGeometryWait = useCallback(
    () => releasePendingPushEvent('geometry_construction'),
    [releasePendingPushEvent]
  );
  const settleDeriveProcessed = useCallback(
    () => handleServerChange('geometry_construction'),
    [handleServerChange]
  );
  const settleDeriveFailed = useCallback(
    () => handleServerFailure(null, 'geometry_construction'),
    [handleServerFailure]
  );
  useGeometryDeriveWait({
    pending: geometryPending,
    pointsHref,
    pointsData,
    refetchPoints,
    onProcessed: settleDeriveProcessed,
    onFailed: settleDeriveFailed,
    onExhausted: releaseGeometryWait,
  });

  const handleEditPoint = (point: WellborePoint) => {
    const index = pointsData?.points.indexOf(point) ?? -1;
    if (index === -1) return;
    setEditingIndex(index);
    setRightPanel('editPoint');
  };
  // The point the editor is on, re-read from the latest points every render so a
  // rebuild underneath the open editor shows the new values instead of a stale copy.
  const editingPoint =
    editingIndex === null ? null : (pointsData?.points[editingIndex] ?? null);
  // Stories 5 and 7: every write answers with the new points and settles here — what
  // to re-read, what to close, and whether the sheet leaves with it.
  const writes = useGeometryWrites({
    wellWorkspaceId,
    uwi14: workspace.uwi14,
    pointsData,
    editingPoint,
    onPointsAnswered: setAnsweredPoints,
    onPointsChanged: () => {
      void refetchPoints();
      if (gridsHref) refetchGrids();
      subsurfaceRef.current?.refreshCharts();
    },
    // The points are saved either way; `failed` means no path could be built from
    // them, which the Explorer's banner then says.
    onGeometrySettled: (status) => {
      if (status === 'failed') {
        handleServerFailure(null, 'geometry_construction');
        // The saved points are still the operator's: re-read them, or the map falls
        // back to the set before the edit and the save reads as a snap back.
        if (pointsHref) refetchPoints();
      } else {
        handleServerChange('geometry_construction');
      }
    },
    onEditorDone: () => {
      setEditingIndex(null);
      setRightPanel((prev) => (prev === 'editPoint' ? null : prev));
    },
    // A committed edit ends editing; a save from the sheet's header also returns to
    // the Well Workspace, which re-reads its Wellbores.
    onCommitted: () => {
      geometryMapRef.current?.endEditing();
      if (!closeAfterCommit.current) return;
      closeAfterCommit.current = false;
      onLeave();
    },
    onCommitFailed: () => {
      closeAfterCommit.current = false;
    },
    onDatumSaved: () => {
      setRightPanel((prev) => (prev === 'propertyDetails' ? null : prev));
      refetchShow();
      void refetchDetails();
    },
  });
  const mapGeometry = useMemo(() => {
    // Only the tearsheet edits geometry; the Well page's host stays read-only.
    if (!embedded) return undefined;
    return {
      points: pointsData?.points ?? [], // empty until served: the map edits the stick
      ...mapPointsState,
      landingPointIndex: pointsData?.landingPointIndex ?? null,
      editable: canEditGeometry,
      canCommit: !!writes.commitHref,
      committing: writes.committing || geometryPending,
      surveyed,
      onCommit: writes.commitGeometry,
    };
  }, [
    embedded,
    pointsData,
    mapPointsState,
    canEditGeometry,
    writes.commitHref,
    writes.committing,
    geometryPending,
    surveyed,
    writes.commitGeometry,
  ]);

  const tasks = taskVerificationItems(allItems);
  // The target-zone verification item drives the section's Mark-Correct toggle
  // (the same verdict shown in the Tasks drawer).
  const targetZoneItem =
    allItems.find((item) => item.path === 'target_zone') ?? null;
  const targetZoneMarkedCorrect = targetZoneItem?.correctnessState === true;
  const {
    confirmState,
    clearState,
    rerunState,
    verdictState,
    confirmFailed,
    clearFailed,
    handleRerunAgent,
    handleConfirmTargetZone,
    handleRemoveTargetZoneCorrect,
    handleManualSelect,
    handleClearSelection,
  } = useTargetZoneActions({
    workspace,
    hrefs: targetZoneHrefs,
    targetZoneItem,
    rebuildSurvey,
    surveyUntouched,
    addPendingPushEvent,
    clearGenerationError: () => setGenerationError(null),
    refetchShow,
    refetchItems,
    refetchSuggestions,
    refreshCharts: () => subsurfaceRef.current?.refreshCharts(),
    onManualSelected: () => setRightPanel(null),
  });
  const completedCount = tasks.filter(
    (item) => item.correctnessState !== null
  ).length;
  // Promote isn't gated on verification — enabled whenever the workspace isn't
  // already promoted (a promoted workspace is locked/read-only).
  const promoteEnabled = !workspace.promoted;
  const editable = workspace.auth.includes('update');

  // The settings PATCH body is wrapped per address: `wellbore` under a Well Workspace
  // bore route, `wellbore_geometry_workspace` otherwise. Keyed on the show's href,
  // since the sheet may hold either.
  const settingsBody = (edit: PreDrillWorkspaceSettingsEdit) => {
    return workspace.href.startsWith('/well_workspaces/')
      ? { wellbore: edit }
      : { wellbore_geometry_workspace: edit };
  };

  const persistColorRules = (next: ColorByRule[]) => {
    updateWorkspace({
      type: 'PATCH',
      url: workspace.href,
      data: settingsBody({
        state: { colorRules: toColorRuleState(next) as unknown as JSONValue },
      }),
      silentLoading: true,
    });
  };

  // Switching the SGC cascades server-side (verdicts reset + geometry stale),
  // so re-fetch the thin show after the PATCH.
  const handleSelectStructureGridCollection = (collectionId: number) => {
    setSavingSgc(true);
    updateWorkspace({
      type: 'PATCH',
      url: workspace.href,
      data: settingsBody({ structure_grid_collection_id: collectionId }),
      onSuccess: () => {
        setSavingSgc(false);
        refetchShow();
      },
      onError: () => setSavingSgc(false),
    });
  };

  const handleColorRulesChange = (next: ColorByRule[]) => {
    setColorRules(next);
    persistColorRules(next);
  };

  // Fetches the color modal's value lists AND the filtered wells in one bbox-
  // scoped /queries/wells call (cached so attribute switches regroup client-side).
  const colorFacetsAbortRef = useRef<AbortController | null>(null);
  const colorByWellsRef = useRef<FilteredUwi[]>([]);
  const fetchColorFacets = useCallback(() => {
    const boundingBox = boundingBoxFromMapRef(mapRef);
    const scope = boundingBox
      ? { boundingBox }
      : { source_uwi: workspace.uwi14.slice(0, 10), radius: COLOR_BY_RADIUS };

    colorFacetsAbortRef.current?.abort();
    const controller = new AbortController();
    colorFacetsAbortRef.current = controller;

    void request
      .post<WellsFacetsResponse, unknown>(
        '/queries/wells',
        {
          query: {
            ...scope,
            facets: COLOR_ATTRIBUTE_FACET_FIELDS,
            includeFilteredUwis: true,
          },
        },
        controller
      )
      .then((response) => {
        if (controller.signal.aborted || response.type !== 'success') return;
        colorByWellsRef.current = response.data.filteredUwis ?? [];
        setColorFacets(response.data.facets ?? null);
      });
  }, [mapRef, workspace.uwi14, setColorFacets]);

  // Builds the paint UWI map by grouping the cached wells by the selected
  // attribute's value (refreshed at modal-open, so it reflects that viewport).
  const resolveColorByUwis = (category: string, values: string[]) => {
    if (!COLOR_ATTRIBUTE_FACET_PARAM[category] || values.length === 0) return;
    const wells = colorByWellsRef.current;

    setColorByUwiMap((prev) => {
      const next = { ...prev };
      values.forEach((value) => {
        next[`${category}:${value}`] = resolveColorByUwiMembership(
          wells,
          category,
          value
        );
      });
      return next;
    });
  };

  const handleColorSelectionChange = (category: string, values: string[]) => {
    // Well Qualifier resolves on the map directly with static options, so it
    // never hits the wells endpoint.
    if (category === WELL_QUALIFIER_ATTRIBUTE) return;
    if (!COLOR_ATTRIBUTE_FACET_PARAM[category]) return;

    // Resolve membership for the selected values (no-op when none selected).
    // The value list is already in the facets atom — no fetch on switch.
    resolveColorByUwis(category, values);
  };

  // Color modal value list from the picker facets (keyed by camelCase id). Well
  // Qualifier keeps its static options; attributes not returned show none.
  const getColorOptions = useCallback(
    (categoryId: string): ColorByOption[] => {
      if (categoryId === WELL_QUALIFIER_ATTRIBUTE) {
        return getPreDrillColorOptions(categoryId);
      }
      return (colorFacets?.[categoryId]?.buckets ?? []).map((bucket) => ({
        id: bucket.id,
        name: bucket.name,
      }));
    },
    [colorFacets]
  );

  // Clear Selection is gated by the resource's `delete` auth.
  const targetZoneClearable =
    workspace.resources.targetZoneSuggestions.auth.includes('delete');
  const toggleTasks = () =>
    setRightPanel((prev) => (prev === 'tasks' ? null : 'tasks'));

  const handlePromote = () => {
    promoteWorkspace({
      type: 'POST',
      url: workspace.resources.promotions.href,
      data: { promotion: { verdict: 'correct' } },
      onSuccess: () => {
        setPromoteModalOpen(false);
        // Navigate back to the page that launched the workspace (same as Back).
        // No refetch needed — we're leaving this (now-promoted, read-only) page.
        onLeave();
      },
    });
  };

  const handleVerdictChange = () => {
    void refetchItems();
    refetchShow();
  };

  // The three geometry sections, built once and ordered by the layout below.
  const mapView = (
    <WorkspaceMap
      ref={geometryMapRef}
      uwi14={workspace.uwi14}
      colorRules={colorRules}
      geometry={mapGeometry}
      onEditingChange={setGeometryEditing}
      onExpandedChange={setMapExpanded}
    />
  );
  const explorer = (
    <SubsurfaceExplorer
      // Stacked only where the Explorer owns a column of its own; in the tearsheet it sits
      // beside the map as two of a row of three, so its views go side by side.
      stacked={!!pointsHref && !embedded}
      onExpandedChange={setExplorerExpanded}
      ref={subsurfaceRef}
      workspace={workspace}
      colorRules={colorRules}
      // The tearsheet has no settings sidebar, so the collection picker opens from
      // the Explorer header instead (UAT 16).
      onOpenGridCollection={
        embedded ? () => setRightPanel('gridCollection') : undefined
      }
      // Beside the geometry map, the right pane is the map's cross-section (UAT 10),
      // carrying this workspace's own edit of the bore (UAT 21).
      crossSectionAlongMap={embedded}
      wellWorkspaceId={wellWorkspaceId ?? null}
      geometryError={
        geometryError ??
        (geometryFailedOnRead
          ? {
              message:
                'The last geometry derive for this wellbore failed. Adjust the points or the depth inputs and run it again.',
            }
          : null)
      }
      permittedDepthTvdFt={permittedDepthTvdFt}
      onRefreshEnqueued={handleGeometryRefreshEnqueued}
      // Story 7 hosts the depth controls in the points panel when the backend
      // serves points; otherwise the Explorer keeps its own, as it always has.
      depthControls={pointsHref ? depthControls : undefined}
      onCanRevertChange={setCanRevert}
      // Story 8: a depthless bore shows its views behind a banner only where the
      // backend can derive the depths; elsewhere the old gate stands.
      ungateDepthless={!!pointsHref || !!gridsHref}
      // Keep Recalculate disabled from click through the Pusher run, until the
      // geometry push lands.
      geometryRefreshing={geometryPending}
      hasPointDepths={pointsHaveDepths}
      // Block Recalculate while a target-zone re-run is in flight (via rerunState
      // / its push); a pending status alone is not a run.
      targetZoneBusy={
        rerunState.loading ||
        pendingPushEvents.includes('target_zone_generation') ||
        (workspace.targetZoneGeologicIntervalId === null &&
          (workspace.targetZoneStatus === 'pending' ||
            workspace.targetZoneStatus === 'processing'))
      }
    />
  );
  const explorerSection = (
    <div hidden={!visibility.subsurfaceExplorer} key="subsurface-explorer">
      {explorer}
    </div>
  );
  const pointsSection =
    pointsData !== null || pointsHref ? (
      <div hidden={!visibility.wellborePoints} key="wellbore-points">
        <WellborePointsPanel
          points={pointsData}
          loading={pointsLoading}
          hasPointDepths={pointsHaveDepths}
          editable={geometryEditable}
          targetTvd={depthControls.tvdInput}
          onTargetTvdChange={depthControls.setTvdInput}
          tvdInvalid={depthControls.tvdOutOfRange}
          useTargetZone={depthControls.gridDataActive}
          targetZoneAvailable={
            depthControls.hasUsableGrid &&
            workspace.targetZoneGeologicIntervalId !== null
          }
          onUseTargetZoneChange={depthControls.setUseGridData}
          keepSurveyDepths={surveyUntouched ? keepSurveyDepths : null}
          onKeepSurveyDepthsChange={setKeepSurveyDepths}
          onUpdateDepths={() =>
            subsurfaceRef.current?.recalculate({
              rebuildSurvey: rebuildSurvey === true,
            })
          }
          updateDisabled={
            !canRefreshGeometry ||
            !depthControls.refreshEnabled ||
            !depthControls.depthValid ||
            depthControls.refreshing ||
            depthControls.recalcSubmitted ||
            geometryPending ||
            writes.committing ||
            writes.pointWriteInFlight
          }
          onRevert={() => subsurfaceRef.current?.revert()}
          revertDisabled={!canRevert || geometryPending}
          onEditPoint={handleEditPoint}
          onDeletePoint={writes.deletePoint}
          onSetLandingPoint={writes.setLandingPoint}
          writeError={writes.pointWriteError}
          onDismissWriteError={writes.dismissPointWriteError}
        />
      </div>
    ) : null;
  const targetZoneSection = (
    <div hidden={!visibility.targetZone} key="target-zone">
      <TargetZoneIdentification
        workspace={workspace}
        suggestions={suggestions}
        // Only the INITIAL load shows the full loading state — a background
        // refetch keeps old data, so gate on `!suggestions`.
        loading={suggestionsResource.loading && !suggestions}
        markedCorrect={targetZoneMarkedCorrect}
        busy={confirmState.loading || verdictState.loading}
        rerunning={
          rerunState.loading ||
          pendingPushEvents.includes('target_zone_generation')
        }
        onConfirm={handleConfirmTargetZone}
        onRemoveCorrect={handleRemoveTargetZoneCorrect}
        onRerunAgent={handleRerunAgent}
        onGetAiSuggestions={handleRerunAgent}
        onOpenChangeMatch={() => setRightPanel('changeMatch')}
        clearable={targetZoneClearable}
        clearing={clearState.loading}
        onClearSelection={handleClearSelection}
        onOpenAgentOutput={() => setRightPanel('agentOutput')}
        onOpenAgentRuns={() => setRightPanel('agentRuns')}
        onOpenGlossary={() => setRightPanel('scoringGlossary')}
        noDirectionalSurvey={noDirectionalSurvey}
        neutralOnPick={embedded}
      />
    </div>
  );

  return (
    <div
      className={classNames(styles.shell, {
        [styles.shellEmbedded]: embedded,
      })}
    >
      {embedded ? (
        <GeometrySheetHeader
          wellName={wellName ?? workspace.name}
          uwi14={workspace.uwi14}
          saving={writes.committing}
          saveDisabled={geometryEditing}
          onBack={onLeave}
          onCancel={() => {
            geometryMapRef.current?.discardPending();
            onLeave();
          }}
          onSave={() => {
            // A waiting map edit is committed and the sheet closes on the 201; with
            // nothing waiting (point edits land as made) Save is simply the way back.
            if (geometryMapRef.current?.hasPendingEdit()) {
              closeAfterCommit.current = true;
              geometryMapRef.current.commitPending();
              return;
            }
            onLeave();
          }}
        />
      ) : (
        <PreDrillWorkspaceHeader
          name={workspace.name}
          subtitle={workspace.uwi14}
          settingsOpen={settingsOpen}
          tasksOpen={rightPanel === 'tasks'}
          taskProgressLabel={`${completedCount}/${tasks.length}`}
          promoteEnabled={promoteEnabled}
          onBack={onLeave}
          onToggleSettings={() => setSettingsOpen((prev) => !prev)}
          onToggleTasks={toggleTasks}
          onPromote={() => setPromoteModalOpen(true)}
        />
      )}

      <DualSidebarLayout
        left={
          embedded
            ? undefined
            : {
                content: (
                  <SettingsPanel
                    structureGridCollectionName={
                      workspace.structureGridCollectionName
                    }
                    structureGridCollectionIsDefault={
                      workspace.structureGridCollectionIsDefault
                    }
                    savingStructureGridCollection={savingSgc}
                    hasRelatedItems={!!workspace.resources.relatedItems}
                    hasWellborePoints={!!pointsHref}
                    visibility={visibility}
                    colorRules={colorRules}
                    disabled={!editable}
                    onVisibilityChange={setVisibility}
                    onColorRulesChange={handleColorRulesChange}
                    onColorSelectionChange={handleColorSelectionChange}
                    getColorOptions={getColorOptions}
                    colorOptionsLoading={colorOptionsLoading}
                    onOpenColorModal={fetchColorFacets}
                    onOpenGridCollection={() => setRightPanel('gridCollection')}
                  />
                ),
                isOpen: settingsOpen,
              }
        }
        right={{
          content: (
            <>
              {rightPanel === 'agentOutput' && suggestions ? (
                <AgentOutputPanel
                  suggestions={suggestions}
                  onClose={() => setRightPanel(null)}
                />
              ) : null}
              {rightPanel === 'agentRuns' ? (
                <AgentRunsPanel
                  uwi14={workspace.uwi14}
                  onClose={() => setRightPanel(null)}
                />
              ) : null}
              {rightPanel === 'scoringGlossary' ? (
                <ScoringGlossary onClose={() => setRightPanel(null)} />
              ) : null}
              {rightPanel === 'gridCollection' ? (
                <SgcPickerDrawer
                  uwi14={workspace.uwi14}
                  selectedId={workspace.structureGridCollectionId}
                  saving={savingSgc}
                  onClose={() => setRightPanel(null)}
                  onSelect={(id) => {
                    handleSelectStructureGridCollection(id);
                    setRightPanel(null);
                  }}
                />
              ) : null}
              {rightPanel === 'changeMatch' ? (
                <ChangeMatchDrawer
                  workspaceId={workspace.id}
                  bore={catalogBore}
                  structureGridCollectionId={
                    workspace.structureGridCollectionId
                  }
                  aiRunHappened={
                    !!suggestions && suggestions.suggestionId !== null
                  }
                  saving={confirmState.loading}
                  intersectedGrids={intersectedGrids}
                  onClose={() => setRightPanel(null)}
                  onSave={handleManualSelect}
                />
              ) : null}
              {rightPanel === 'landingInterval' && landingPicker ? (
                <SelectGeologicIntervalDrawer
                  request={landingPicker}
                  onClose={() => setRightPanel(null)}
                  onSave={(row) => {
                    landingPicker.onPick(row);
                    setRightPanel(null);
                  }}
                />
              ) : null}
              {rightPanel === 'editPoint' && editingPoint ? (
                <EditWellborePointPanel
                  // The fields seed once, so a point whose values move under the
                  // editor gets a fresh one rather than the old values to save back.
                  key={`${editingIndex}-${editingPoint.id}-${editingPoint.latitude}-${editingPoint.longitude}-${editingPoint.tvdFt}`}
                  point={editingPoint}
                  datumElevationFt={pointsData?.datumElevationFt ?? null}
                  saving={writes.pointSaving}
                  onClose={() => {
                    setEditingIndex(null);
                    setRightPanel(null);
                  }}
                  onSave={writes.savePoint}
                />
              ) : null}
              {/* Only once the elevations have loaded: the drawer seeds its fields on
                  mount, so opening it mid-flight would offer two empty boxes and save
                  nulls over a recorded datum. The key remounts it on a new read. */}
              {rightPanel === 'propertyDetails' && pointsData ? (
                <PropertyDetailsDrawer
                  key={`${pointsData.groundLevelFt}-${pointsData.datumElevationFt}`}
                  groundLevelFt={pointsData.groundLevelFt}
                  demGroundLevelFt={pointsData.demGroundLevelFt}
                  kbElevationFt={
                    pointsData.datumReference === 'kb'
                      ? pointsData.datumElevationFt
                      : null
                  }
                  kbElevationEstimated={pointsData.kbElevationEstimated}
                  saving={writes.datumSaving}
                  canSave={
                    pointsData.resources.datumUpdates?.auth.includes(
                      'create'
                    ) ?? false
                  }
                  onClose={() => setRightPanel(null)}
                  onSave={writes.saveDatum}
                />
              ) : null}
              {rightPanel === 'tasks' ? (
                <VerificationTasksDrawer
                  items={tasks}
                  onClose={() => setRightPanel(null)}
                  onVerdictChange={handleVerdictChange}
                />
              ) : null}
            </>
          ),
          isOpen: rightPanel !== null,
          // Change Match, the point editor and Property Details take the mock's
          // widths; every other right panel keeps the default (420px).
          width: PANEL_WIDTHS[rightPanel ?? ''],
        }}
      >
        {/* Row so the export drawer compresses the whole content column from the
            RIGHT (full height), consistent with the type-curve page. */}
        <div className={styles.contentRow}>
          <div className={styles.body}>
            <ContentBanners
              confirmFailed={confirmFailed}
              clearFailed={clearFailed}
              readOnlyReason={
                pointsData && !pointsData.editable
                  ? pointsData.lockReason
                  : undefined
              }
              geometryEditFailed={writes.geometryEditFailed}
              onDismissGeometryEditFailure={writes.dismissGeometryEditFailure}
              datumUpdateError={writes.datumUpdateError}
              onDismissDatumUpdateFailure={writes.dismissDatumUpdateFailure}
              generationError={generationError}
            />
            {/* Section visibility is pure show/hide via `hidden` (CSS) — components
              stay mounted so toggling never re-fires their sub-resource fetches. */}
            <div hidden={!visibility.details}>
              <WellboreDetailsCard
                href={workspace.resources.wellboreDetails.href}
                detailsAtom={pointsHref ? detailsAtom : undefined}
                kbElevationEstimated={pointsData?.kbElevationEstimated ?? true}
                onEditDetails={
                  pointsHref
                    ? () => setRightPanel('propertyDetails')
                    : undefined
                }
              />
            </div>
            {/* Where the backend serves points the sections follow the tearsheet mock: the
                map beside the Explorer, then the rest. Otherwise the old order. */}
            {pointsHref ? (
              <GeometryViewsRow
                map={mapView}
                explorer={explorer}
                mapVisible={visibility.map}
                explorerVisible={visibility.subsurfaceExplorer}
                mapExpanded={mapExpanded}
                explorerExpanded={explorerExpanded}
                // The tearsheet puts all three in one row: map, 2D Profile, Cross-Section.
                singleRow={embedded}
              />
            ) : (
              <div hidden={!visibility.map}>{mapView}</div>
            )}
            {pointsHref
              ? [pointsSection, targetZoneSection]
              : [targetZoneSection, explorerSection]}
            {/* INS-12936: below the geometry work, at the bore address only. */}
            {pointsHref &&
              wellWorkspaceId &&
              workspace.resources.landingInterpretation && (
                <LandingInterpretationSection
                  workspace={workspace}
                  wellWorkspaceId={wellWorkspaceId}
                  points={pointsData}
                  pointsHaveDepths={pointsHaveDepths}
                  pointsLoading={pointsLoading}
                  onChanged={refetchShow}
                  onOpenIntervalPicker={(request) => {
                    setLandingPicker(request);
                    setRightPanel('landingInterval');
                  }}
                />
              )}
            {workspace.resources.relatedItems && (
              <div hidden={!visibility.relatedFiles}>
                <RelatedItemsSection
                  host={{
                    basePath: workspace.resources.relatedItems.href,
                    notifications:
                      workspace.resources.relatedItems.notifications,
                  }}
                  readOnly={
                    !workspace.resources.relatedItems.auth.includes('update')
                  }
                />
              </div>
            )}
          </div>
          <ExportImageDrawer />
        </div>
      </DualSidebarLayout>

      <PromoteModal
        open={promoteModalOpen}
        workspaceName={workspace.name}
        loading={promoteState.loading}
        error={promoteState.result?.type === 'error'}
        onClose={() => setPromoteModalOpen(false)}
        onConfirm={handlePromote}
      />
    </div>
  );
};
